/**
 * Local storage utility for ride view layout state
 * Stores layout configuration (columns, panels) and panel visibility
 * Compatible with ResizableLayout's rideViewLayout format
 */

const LAYOUT_KEY = 'rideViewLayout';
const PANEL_STATES_KEY = 'rideViewPanelStates';
const CURRENT_LAYOUT_CONFIG_ID_KEY = 'currentRideViewLayoutConfigId';

/**
 * Save layout state to localStorage
 * @param {Object} layoutState - Layout state: { columns, panels, rideViewPanelStates?, currentRideViewLayoutConfigId? }
 */
export function saveLayoutState(layoutState) {
  try {
    const { rideViewPanelStates, currentRideViewLayoutConfigId, ...rest } = layoutState;
    const layoutData = {
      columns: rest.columns || [],
      panels: rest.panels || [],
    };
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(layoutData));
    if (rideViewPanelStates) {
      localStorage.setItem(PANEL_STATES_KEY, JSON.stringify(rideViewPanelStates));
    }
    if (currentRideViewLayoutConfigId !== undefined) {
      localStorage.setItem(CURRENT_LAYOUT_CONFIG_ID_KEY, currentRideViewLayoutConfigId || '');
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to save layout state:', error);
    }
    throw error;
  }
}

/**
 * Load layout state from localStorage
 * @returns {Object|null} Saved layout state or null if not found
 */
export function loadLayoutState() {
  try {
    const layoutJson = localStorage.getItem(LAYOUT_KEY);
    const panelStatesJson = localStorage.getItem(PANEL_STATES_KEY);
    const configId = localStorage.getItem(CURRENT_LAYOUT_CONFIG_ID_KEY);

    const layoutData = layoutJson ? JSON.parse(layoutJson) : null;
    const rideViewPanelStates = panelStatesJson ? JSON.parse(panelStatesJson) : null;

    if (!layoutData && !rideViewPanelStates && !configId) {
      return null;
    }

    return {
      ...(layoutData || {}),
      ...(rideViewPanelStates && { rideViewPanelStates }),
      ...(configId !== null && configId !== '' && { currentRideViewLayoutConfigId: configId }),
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to load layout state:', error);
    }
    return null;
  }
}

/**
 * Get the ID of the currently applied layout configuration
 * @returns {string|null} Current layout config ID or null
 */
export function getCurrentLayoutConfigId() {
  try {
    const id = localStorage.getItem(CURRENT_LAYOUT_CONFIG_ID_KEY);
    return id || null;
  } catch (error) {
    return null;
  }
}
